/**
 * Order API
 *
 * API layer for order-related endpoints.
 * Handles all HTTP communication for orders.
 */

import { AxiosResponse, AxiosError } from 'axios';
import { apiClient } from './api-client';
import {
  CreateOrderPayload,
  CreateOrderSuccessResponse,
  CreateOrderErrorResponse,
} from './types/order-types';

/**
 * API endpoints
 */
const ENDPOINTS = {
  CREATE_ORDER: '/api/v1/order',
} as const;

/**
 * Order API methods
 */
export const orderApi = {
  /**
   * Create a new order
   *
   * @param payload - Order creation payload
   * @returns Promise resolving to the API response
   * @throws AxiosError if the request fails
   */
  createOrder: async (
    payload: CreateOrderPayload
  ): Promise<CreateOrderSuccessResponse> => {
    const response: AxiosResponse<CreateOrderSuccessResponse> = await apiClient.post(
      ENDPOINTS.CREATE_ORDER,
      payload
    );
    return response.data;
  },
};

/**
 * Extract error message from API error response
 */
export function getApiErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    // Handle API error response
    if (error.response?.data) {
      const errorData = error.response.data as CreateOrderErrorResponse;
      return errorData.message || errorData.details || 'An error occurred';
    }

    // Handle network errors
    if (error.code === 'ECONNABORTED') {
      return 'Request timeout. Please try again.';
    }

    if (error.code === 'ERR_NETWORK') {
      return 'Network error. Please check your connection.';
    }
  }

  // Handle generic errors
  if (error instanceof Error) {
    return error.message;
  }

  return 'An unexpected error occurred';
}

/**
 * Extract error status code from API error
 */
export function getApiErrorStatusCode(error: unknown): number | undefined {
  if (error instanceof AxiosError) {
    return error.response?.status;
  }
  return undefined;
}

export default orderApi;
